import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useCreateOrder,
  useDeleteOrder,
  useMarkOrderPaid,
  useOrderDetail,
  useOrderList,
  useOrderPackages,
  useUpdateOrder,
} from '../hooks';
import type { OrderFilters, OrderFormValues, OrderRecord } from '../types';
import { OrderTable } from '../components/OrderTable';
import { OrderFormModal } from '../components/OrderFormModal';
import { OrderDetailDrawer } from '../components/OrderDetailDrawer';

const defaultFilters: OrderFilters = {
  status: 'all',
  search: '',
};

export function OrderListPage() {
  const [filters, setFilters] = useState<OrderFilters>(defaultFilters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isFormOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [isDetailOpen, setDetailOpen] = useState(false);

  const { data: orders = [], isLoading } = useOrderList(filters);
  const { data: packages = [], isLoading: isLoadingPackages } = useOrderPackages();
  const { data: editingRecord, isFetching: isFetchingEditing } = useOrderDetail(editingId ?? undefined, formMode === 'edit' && isFormOpen);
  const { data: detailRecord } = useOrderDetail(detailId ?? undefined, isDetailOpen && !!detailId);

  const createMutation = useCreateOrder();
  const updateMutation = useUpdateOrder();
  const deleteMutation = useDeleteOrder();
  const markPaidMutation = useMarkOrderPaid();

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (record: OrderRecord) => {
    setFormMode('edit');
    setEditingId(record.id);
    setFormOpen(true);
  };

  const handleView = (record: OrderRecord) => {
    setDetailId(record.id);
    setDetailOpen(true);
  };

  const handleMarkPaid = (record: OrderRecord) => {
    if (record.status === 'completed') return;
    const confirmed = window.confirm(`Đánh dấu đơn ${record.code} đã thanh toán?`);
    if (!confirmed) return;
    markPaidMutation.mutate(record.id);
  };

  const handleDelete = (record: OrderRecord) => {
    if (record.status !== 'pending') {
      toast.error('Chỉ hủy được đơn trạng thái chờ xử lý');
      return;
    }
    const confirmed = window.confirm(`Bạn có chắc muốn hủy đơn ${record.code}?`);
    if (!confirmed) return;
    deleteMutation.mutate(record.id);
  };

  const handleBulkCancel = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(`Hủy ${selectedIds.length} đơn đã chọn?`);
    if (!confirmed) return;
    try {
      await Promise.all(selectedIds.map((id) => deleteMutation.mutateAsync(id)));
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = (values: OrderFormValues) => {
    if (formMode === 'create') {
      createMutation.mutate(values, {
        onSuccess: () => {
          setFormOpen(false);
        },
      });
    } else if (editingId) {
      updateMutation.mutate(
        {
          id: editingId,
          payload: {
            order_items: values.order_items,
            notes: values.notes,
          },
        },
        {
          onSuccess: () => {
            setFormOpen(false);
          },
        }
      );
    }
  };

  const editingInitialValues = useMemo(() => {
    if (!editingRecord) return undefined;
    return {
      orderType: 'existing' as const,
      customer_id: editingRecord.customers_id,
      notes: editingRecord.notes,
      order_items:
        editingRecord.order_details?.map((detail) => ({
          package_id: detail.package_id,
          quantity: detail.quantity,
        })) ?? [],
    };
  }, [editingRecord]);

  return (
    <div className="card">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
        <div>
          <h3 className="card-title mb-1">Quản lý đơn hàng</h3>
          <p className="card-subtitle mb-0">
            Tạo đơn offline, theo dõi trạng thái thanh toán và liên kết với subscriptions của khách hàng.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={selectedIds.length === 0 || deleteMutation.isLoading}
            onClick={handleBulkCancel}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-trash" style={{ fontSize: 16, marginRight: 6 }} />
            Hủy đã chọn
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleOpenCreate}
            disabled={isLoadingPackages}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-plus" style={{ fontSize: 16, marginRight: 6 }} />
            Tạo đơn mới
          </button>
        </div>
      </div>

      <OrderTable
        data={orders}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={setFilters}
        onView={handleView}
        onEdit={handleOpenEdit}
        onMarkPaid={handleMarkPaid}
        onDelete={handleDelete}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <OrderFormModal
        open={isFormOpen}
        mode={formMode}
        packages={packages}
        initialValues={editingInitialValues}
        submitting={createMutation.isLoading || updateMutation.isLoading || isFetchingEditing}
        editingRecord={editingRecord}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <OrderDetailDrawer open={isDetailOpen} record={detailRecord} onClose={() => setDetailOpen(false)} />
    </div>
  );
}


